import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Vote, LogIn, ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Berhasil masuk");
      nav("/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-batik">
      {/* Left: brand panel */}
      <div className="md:w-1/2 lg:w-3/5 bg-slate-900 text-white px-8 md:px-16 py-12 md:py-20 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-12" data-testid="back-to-public">
            <ArrowLeft className="h-4 w-4" /> Kembali ke beranda
          </Link>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-md bg-white text-slate-900 grid place-items-center">
              <Vote className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold">SIPILKADES</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Sistem Informasi DPT</div>
            </div>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] max-w-xl">
            Pemutakhiran daftar pemilih, terpadu &amp; akuntabel.
          </h1>
          <p className="mt-6 text-slate-300 max-w-lg leading-relaxed">
            Sistem resmi untuk pengelolaan DPS &amp; DPT Pemilihan Kepala Desa.
            Mendukung Coklit lapangan, verifikasi berlapis, dan audit trail penuh.
          </p>
        </div>
        <div className="relative mt-12 text-xs uppercase tracking-[0.3em] text-slate-400">
          Desa Mekarsari · Kab. Bekasi · Jawa Barat
        </div>
      </div>

      {/* Right: form */}
      <div className="md:w-1/2 lg:w-2/5 bg-white flex items-center justify-center px-6 py-12 md:py-20">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Masuk Akun</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Selamat datang</h2>
            <p className="text-sm text-slate-600 mt-2">Gunakan akun yang diberikan oleh administrator desa.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="cth: admin"
                autoComplete="username"
                required
                className="h-12"
                data-testid="login-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="h-12"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              data-testid="login-submit-button"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
